// Print/Export Constants and Default Configurations
// Sprint 1: Foundation - Constants Module

import type { 
  PrintConfig, 
  PrintPreset, 
  PrintMetadata,
  PrintPageSize,
} from '@/app/types/print';
import {
  DEFAULT_PRINT_AREA,
  DEFAULT_PDF_OPTIONS,
  DEFAULT_EXCEL_OPTIONS,
  DEFAULT_PREVIEW_SETTINGS,
} from '@/app/types/print';

// ===== Version =====
export const PRINT_CONFIG_VERSION = 1;

// ===== Storage Keys =====
export const STORAGE_KEYS = {
  PRINT_CONFIG: 'cad_printConfig',
  PRINT_PRESETS: 'cad_printPresets',
  LAST_PRINT_JOB: 'cad_lastPrintJob',
} as const;

// ===== Default Metadata Values =====
export const DEFAULT_METADATA: Omit<PrintMetadata, 'datePrinted'> = {
  projectName: 'Untitled Project',
  projectDescription: '',
  author: '',
  dateCreated: new Date(),
  dateModified: new Date(),
  version: '1.0',
  companyName: '',
  customFields: {},
};

// ===== Default Print Configuration =====
export function createDefaultPrintConfig(): PrintConfig {
  return {
    version: PRINT_CONFIG_VERSION,
    area: DEFAULT_PRINT_AREA,
    format: {
      format: 'pdf',
      pdf: DEFAULT_PDF_OPTIONS,
      excel: DEFAULT_EXCEL_OPTIONS,
    },
    metadata: {
      ...DEFAULT_METADATA,
      datePrinted: new Date(),
    },
    preview: DEFAULT_PREVIEW_SETTINGS,
    isCustomPreset: false,
  };
}

// ===== Built-in Print Presets =====
export const BUILT_IN_PRESETS: PrintPreset[] = [
  {
    id: 'preset-full-table-pdf-a4',
    name: 'Gesamte Tabelle - PDF (A4)',
    description: 'Gesamte Tabelle als PDF im A4-Format exportieren',
    isBuiltIn: true,
    createdAt: new Date('2025-01-26'),
    modifiedAt: new Date('2025-01-26'),
    config: {
      area: {
        type: 'current-table',
        includeHeaders: true,
        includeHeaderRows: true,
      },
      format: {
        format: 'pdf',
        pdf: {
          ...DEFAULT_PDF_OPTIONS,
          pageSize: 'A4',
          orientation: 'landscape',
        },
      },
    },
  },
  {
    id: 'preset-full-table-pdf-a3',
    name: 'Gesamte Tabelle - PDF (A3)',
    description: 'Gesamte Tabelle als PDF im A3-Format exportieren (groß)',
    isBuiltIn: true,
    createdAt: new Date('2025-01-26'),
    modifiedAt: new Date('2025-01-26'),
    config: {
      area: {
        type: 'current-table',
        includeHeaders: true,
        includeHeaderRows: true,
      },
      format: {
        format: 'pdf',
        pdf: {
          ...DEFAULT_PDF_OPTIONS,
          pageSize: 'A3',
          orientation: 'landscape',
        },
      },
    },
  },
  {
    id: 'preset-full-table-pdf-letter',
    name: 'Gesamte Tabelle - PDF (Letter)',
    description: 'Gesamte Tabelle als PDF im US Letter-Format exportieren',
    isBuiltIn: true,
    createdAt: new Date('2025-01-26'),
    modifiedAt: new Date('2025-01-26'),
    config: {
      area: {
        type: 'current-table',
        includeHeaders: true,
        includeHeaderRows: true,
      },
      format: {
        format: 'pdf',
        pdf: {
          ...DEFAULT_PDF_OPTIONS,
          pageSize: 'Letter',
          orientation: 'landscape',
        },
      },
    },
  },
  {
    id: 'preset-full-table-excel',
    name: 'Gesamte Tabelle - Excel (CSV)',
    description: 'Gesamte Tabelle als CSV-Datei exportieren',
    isBuiltIn: true,
    createdAt: new Date('2025-01-26'),
    modifiedAt: new Date('2025-01-26'),
    config: {
      area: {
        type: 'current-table',
        includeHeaders: true,
        includeHeaderRows: true,
      },
      format: {
        format: 'excel-csv',
        excel: {
          separator: ',',
          includeFormulas: false,
          includeStyles: false,
          includeProjectMetadata: true,
        },
      },
    },
  },
  {
    id: 'preset-selection-only-pdf',
    name: 'Ausgewählter Bereich - PDF',
    description: 'Nur ausgewählte Zellen als PDF exportieren',
    isBuiltIn: true,
    createdAt: new Date('2025-01-26'),
    modifiedAt: new Date('2025-01-26'),
    config: {
      area: {
        type: 'selected-range',
        includeHeaders: true,
        includeHeaderRows: false,
      },
      format: {
        format: 'pdf',
        pdf: {
          ...DEFAULT_PDF_OPTIONS,
          pageSize: 'Auto',
        },
      },
    },
  },
  {
    id: 'preset-compact-pdf',
    name: 'Kompaktes PDF',
    description: 'Minimale Ränder, keine Metadaten, kleine Schrift',
    isBuiltIn: true,
    createdAt: new Date('2025-01-26'),
    modifiedAt: new Date('2025-01-26'),
    config: {
      area: {
        type: 'current-table',
        includeHeaders: true,
        includeHeaderRows: true,
      },
      format: {
        format: 'pdf',
        pdf: {
          ...DEFAULT_PDF_OPTIONS,
          margins: { top: 10, right: 10, bottom: 10, left: 10 },
          fontSize: 8,
          includeMetadataHeader: false,
          includePageNumbers: false,
        },
      },
    },
  },
  {
    id: 'preset-portrait-a5',
    name: 'Kompakt - PDF (A5 Hochformat)',
    description: 'Kleinformatiger Export für kompakte Ausdrucke',
    isBuiltIn: true,
    createdAt: new Date('2025-01-26'),
    modifiedAt: new Date('2025-01-26'),
    config: {
      area: {
        type: 'current-table',
        includeHeaders: true,
        includeHeaderRows: true,
      },
      format: {
        format: 'pdf',
        pdf: {
          ...DEFAULT_PDF_OPTIONS,
          pageSize: 'A5',
          orientation: 'portrait',
          fontSize: 8,
        },
      },
    },
  },
];

// ===== Page Size Information =====
export const PAGE_SIZE_INFO: Record<PrintPageSize, { name: string; description: string }> = {
  'A3': {
    name: 'A3',
    description: '297 × 420 mm (ISO, groß)',
  },
  'A4': {
    name: 'A4',
    description: '210 × 297 mm (ISO, Standard)',
  },
  'A5': {
    name: 'A5',
    description: '148 × 210 mm (ISO, klein)',
  },
  'Letter': {
    name: 'Letter',
    description: '8,5 × 11 in (US-Standard)',
  },
  'Legal': {
    name: 'Legal',
    description: '8,5 × 14 in (US, lang)',
  },
  'Tabloid': {
    name: 'Tabloid',
    description: '11 × 17 in (US, groß)',
  },
  'Auto': {
    name: 'Auto',
    description: 'An Inhalt anpassen',
  },
};

// ===== Export File Name Generator =====
export function generateExportFileName(
  projectName: string,
  format: 'pdf' | 'excel-csv',
  timestamp = new Date()
): string {
  const sanitizedName = projectName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  const dateStr = timestamp.toISOString().split('T')[0]; // YYYY-MM-DD
  const extension = format === 'pdf' ? 'pdf' : 'csv';
  
  return `${sanitizedName}_table_${dateStr}.${extension}`;
}

// ===== Validation Constants =====
export const VALIDATION = {
  MIN_FONT_SIZE: 6,
  MAX_FONT_SIZE: 24,
  MIN_MARGIN: 0,
  MAX_MARGIN: 50,
  MIN_ZOOM: 0.25,
  MAX_ZOOM: 3.0,
} as const;